import React, { useState, useEffect, useCallback } from 'react'

import moment from 'moment'
import PropTypes from 'prop-types'

import debounce from 'lodash/debounce'
import forEach from 'lodash/forEach'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import omit from 'lodash/omit'

import { Grid } from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/DeleteOutlined'

import { Scope } from '@galo-monitor/forms'
import { momentBackDateFormat } from '@galo-monitor/utils/dates'
import IconButton from '@galo-monitor/web-components/IconButton'
import InputDate from '@galo-monitor/web-components/InputDate'
import InputUnit from '@galo-monitor/web-components/InputUnit'

import { Flex, LinkText, SectionTitle } from '../../theme'
import {
  formatMachinesArray,
  MACHINE_INITIAL_VALUE
} from '../../utils/formatters'
import {
  initialMachinesOptions,
  initialResponsibleOptions
} from '../../utils/initialOptions'
import {
  handleFetchMachineOptions,
  handleFetchResponsibleOptions
} from '../../utils/requests'
import CustomInputSearch from '../CustomInputSearch'

const DEBOUNCE_TIME_IN_MS = 500

const HarvestFields = ({ activity, loading, stateTypeButtons, t, unitInitials }) => {
  const [searchMachineParams, setSearchMachineParams] = useState({ name: '' })
  const [searchResponsibleParams, setSearchResponsibleParams] = useState({ name: '' })
  const [machinesInputs, setMachinesInputs] = useState({ 0: MACHINE_INITIAL_VALUE() })

  const onMachineKeyUp = useCallback(debounce(
    value => {
      setSearchMachineParams({
        name: value
      })
    },
    DEBOUNCE_TIME_IN_MS
  ), [])

  const onResponsibleKeyUp = debounce(
    value => {
      setSearchResponsibleParams({
        name: value
      })
    },
    DEBOUNCE_TIME_IN_MS
  )

  const onNewMachine = () => {
    const currentIndex = Object.keys(machinesInputs).pop()

    setMachinesInputs(prevState => ({
      ...prevState, [Number(currentIndex) + 1]: MACHINE_INITIAL_VALUE()
    }))
  }

  const onRemoveMachine = index =>
    setMachinesInputs(prevState => omit(prevState, [index]))

  useEffect(() => {
    if (!isEmpty(activity.machines)) {
      const realizedMachines = []
      const plannedMachines = []

      forEach(activity.machines, rawMaterial => {
        if (rawMaterial.type === 'realized') {
          realizedMachines.push(rawMaterial)
          return
        }

        plannedMachines.push(rawMaterial)
      })

      setMachinesInputs(
        formatMachinesArray(
          isEmpty(realizedMachines)
            ? plannedMachines
            : realizedMachines
        )
      )
    }
  }, [activity.machines])

  return (
    <Grid container spacing={ 3 } style={ { margin: 0 } }>
      <Grid item xs={ 12 }>
        <SectionTitle variant="h6">
          {t('about the operation')}
        </SectionTitle>
      </Grid>
      <Grid item xs={ 12 }>
        {stateTypeButtons}
      </Grid>
      <Grid item xs={ 12 } sm={ 5 }>
        <InputDate
          name="date_initial"
          label={ t('start date') }
          defaultValue={
            moment(activity.dateInitialRealized ?? activity.dateInitialPlanned)
              .format(momentBackDateFormat)
          }
          disabled={ loading }
          fullWidth
        />
      </Grid>
      <Grid item xs={ 12 } sm={ 5 }>
        <InputDate
          name="date_finish"
          label={ t('end date') }
          defaultValue={
            moment(activity.dateFinishRealized ?? activity.dateFinishPlanned)
              .format(momentBackDateFormat)
          }
          disabled={ loading }
          fullWidth
        />
      </Grid>
      <Grid item xs={ 12 } sm>
        <InputUnit
          name="area_total"
          label={ t('total area') }
          defaultValue={ activity.areaTotalRealized?.toString() ?? activity.areaTotalPlanned?.toString() ?? '' }
          disabled={ loading }
          unit={ unitInitials }
          fullWidth
        />
      </Grid>
      <Grid item xs={ 12 }>
        <SectionTitle variant="h6">
          {t('gross production')}
        </SectionTitle>
      </Grid>
      <Grid item xs={ 12 } sm>
        <InputUnit
          name="production_total_gross"
          label={ t('gross production') }
          unit="Kg"
          defaultValue={ activity.productionTotalGrossRealized?.toString() ?? activity.productionTotalGrossPlanned?.toString() ?? '' }
          disabled={ loading }
          fullWidth
        />
      </Grid>
      <Grid item xs={ 12 }>
        <SectionTitle variant="h6">
          {t('machines and implements')}
        </SectionTitle>
      </Grid>
      {map(machinesInputs, (machine, index) => (
        <Scope path={ `machines[${ index }]` } key={ index }>
          <Grid item xs={ 11 } sm={ 11 }>
            <CustomInputSearch
              name="machine_id"
              label={ t('machine', { howMany: 1 }) }
              onKeyUp={ evt => onMachineKeyUp(evt.target.value) }
              options={ handleFetchMachineOptions }
              queryParams={ searchMachineParams }
              initialOptions={ isEmpty(activity) ? [] : initialMachinesOptions(activity.machines) }
              defaultValue={ machine.id?.toString() ?? '' }
              disabled={ loading }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 1 } sm={ 1 }>
            <Flex>
              <IconButton
                size="small"
                disabled={ Number(index) === 0 }
                onClick={ () => onRemoveMachine(index) }
              >
                <DeleteIcon color="action" />
              </IconButton>
            </Flex>
          </Grid>
        </Scope>
      ))}
      <Grid item xs={ 12 } style={ { marginBottom: 10 } }>
        <LinkText onClick={ onNewMachine }>
          {t('add another')}
        </LinkText>
      </Grid>
      <Grid item xs={ 12 }>
        <SectionTitle variant="h6">
          {t('responsible')}
        </SectionTitle>
      </Grid>
      <Grid item xs={ 12 }>
        <CustomInputSearch
          name="user_responsible_id"
          label={ t('collaborator', { howMany: 1 }) }
          onKeyUp={ evt => onResponsibleKeyUp(evt.target.value) }
          options={ handleFetchResponsibleOptions }
          queryParams={ searchResponsibleParams }
          initialOptions={ isEmpty(activity) ? [] : initialResponsibleOptions(activity) }
          defaultValue={
            activity.userResponsibleIdRealized?.toString() ??
            activity.userResponsibleIdPlanned?.toString() ??
            ''
          }
          disabled={ loading }
          fullWidth
        />
      </Grid>
    </Grid>
  )
}

HarvestFields.propTypes = {
  activity: PropTypes.object,
  loading: PropTypes.bool.isRequired,
  stateTypeButtons: PropTypes.oneOfType([PropTypes.element, PropTypes.object]),
  t: PropTypes.func.isRequired,
  unitInitials: PropTypes.string.isRequired
}

HarvestFields.defaultProps = {
  activity: {},
  stateTypeButtons: <></>
}

export default HarvestFields
